/**
 * Rascunho dos cadastros maiores: o que foi digitado fica guardado no navegador
 * (por usuário e formulário) e pode ser recuperado se a janela fechar por engano.
 */
import { useEffect, useRef, useState } from "react";

const PREFIX = "vg:rascunho:";
const MAX_AGE = 7 * 24 * 3600_000;

interface Stored<T> { at: number; value: T }

function read<T>(key: string): Stored<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stored<T>;
    if (!s || Date.now() - s.at > MAX_AGE) { localStorage.removeItem(PREFIX + key); return null; }
    return s;
  } catch { return null; }
}

export function clearDraft(key: string): void {
  try { localStorage.removeItem(PREFIX + key); } catch { /* sem armazenamento */ }
}

/**
 * useDraft("pessoa:nova", valorAtual, restaurar, ativo)
 * Guarda o valor (1s depois da última alteração) quando `active` e o valor mudou do inicial.
 * Devolve o banner para recuperar/descartar um rascunho existente.
 */
export function useDraft<T>(key: string, value: T, restore: (v: T) => void, active = true): { banner: JSX.Element | null; clear: () => void } {
  const [pending, setPending] = useState<Stored<T> | null>(() => (active ? read<T>(key) : null));
  const initial = useRef(JSON.stringify(value));
  const decided = useRef(!pending);
  const json = JSON.stringify(value);
  useEffect(() => {
    if (!active || !decided.current || json === initial.current) return;
    const t = setTimeout(() => { try { localStorage.setItem(PREFIX + key, JSON.stringify({ at: Date.now(), value })); } catch { /* cheio ou bloqueado */ } }, 1000);
    return () => clearTimeout(t);
  }, [json, key, active]); // eslint-disable-line react-hooks/exhaustive-deps
  const clear = () => clearDraft(key);
  const banner = pending ? (
    <div className="draft-banner" data-no-dirty>
      <span>Há um rascunho não salvo de {new Date(pending.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.</span>
      <button className="btn small primary" onClick={() => { restore(pending.value); decided.current = true; setPending(null); }}>Recuperar</button>
      <button className="btn small ghost" onClick={() => { clearDraft(key); decided.current = true; setPending(null); }}>Descartar</button>
    </div>
  ) : null;
  return { banner, clear };
}
